import { Link } from 'react-router-dom';
import classnames from 'classnames';
import compact from 'lodash/compact';

import style from '../VaccinationTable.module.scss';

import { DateFormat, DateHelper } from '@utils/DateHelper';
import { getAgeDifference } from '@utils/getAgeDifference';

import { getDocumentName } from '../../../helpers/getDocumentName';

import { Icon, IconType } from '@components/Icon';
import { VacciantionData, VaccinationDocument } from '../VaccinationTable';
import { translate } from '@chaika/i18n/helper';

interface Props {
    vaccination: VacciantionData;
    ehrBirthDate?: Date;
}

export function VaccinationRow({ ehrBirthDate, vaccination }: Props): React.ReactElement<any> {
    const ageDifference = getAgeDifference(ehrBirthDate, vaccination.vaccinationDate);

    return (
        <div
            key={vaccination.id}
            className={style.row}
            onClick={vaccination.onVaccinationClick}
        >
            <span className={classnames([style.item, style.vaccinationDate])}>
                {DateHelper.format(vaccination.vaccinationDate, DateFormat.DateWithDotSeparator)}
            </span>
            <span className={classnames([style.item, style.birthDate])}>{ageDifference}</span>
            <span className={classnames([style.item, style.vaccine])}>{vaccination.vaccine}</span>
            <span className={classnames([style.item, style.dose])}>{vaccination.dose}</span>
            <span className={classnames([style.item, style.series])}>{vaccination.series}</span>
            <span className={classnames([style.item, style.expirationDate])}>
                {DateHelper.format(vaccination.expirationDate, DateFormat.DateWithDotSeparator)}
            </span>
            <span className={classnames([style.item, style.stage])}>{vaccination.stage}</span>
            <span className={classnames([style.item, style.deliveryMethod])}>
                {vaccination.injectionSite || vaccination.deliveryMethod
                    ? compact([vaccination.injectionSite, vaccination.deliveryMethod]).join(', ')
                    : '-'}
            </span>
            <span className={classnames([style.item, style.reaction])}>{vaccination.reaction ?? '-'}</span>
            <span
                className={classnames([style.item, style.clinic])}
                title={vaccination.clinic}
            >
                {vaccination.clinic ?? '-'}
            </span>
            <span className={classnames([style.item, style.entry])}>
                {vaccination.parentEntryId ? (
                    <Link
                        className={style.link}
                        to={`/patient/${vaccination.ehrId}/entry/${vaccination.parentEntryId}`}
                        target={'_blank'}
                    >
                        <span>{translate('open')}</span>
                        <Icon type={IconType.OPEN_LINK} />
                    </Link>
                ) : (
                    '-'
                )}
            </span>
            <span className={classnames([style.item, style.documents])}>
                {vaccination.documents.length > 0
                    ? vaccination.documents.map(document => (
                          <DocumentLink
                              key={document.path}
                              path={document.path}
                              name={document.name}
                              onShowDocument={document.onShowDocument}
                          />
                      ))
                    : '-'}
            </span>
            <span
                className={classnames([style.item, style.comment])}
                title={vaccination.comment}
            >
                {vaccination.comment || '-'}
            </span>
        </div>
    );
}

function DocumentLink({ name, onShowDocument }: VaccinationDocument): React.ReactElement<any> {
    return (
        <a
            className={style.link}
            onClick={onShowDocument}
        >
            {getDocumentName(name, 15)}
        </a>
    );
}
